import { ReactNode, useState, MouseEvent } from 'react';
import styled from 'styled-components';
import { Flex, FlexCss, FlexGrowCss } from '../Flex';
import { Logo } from '../Logo';
import {
  isUserWithRoles,
  useAuth, useCombinedUser, useIsAuth, useIsMsalAuth, useIsUserAuth, UserRoles, UserType,
} from '../../authentication';
import { OutsideClicker } from '../OutsideClicker';
import { useProgrammaticLink, useStickySafeNavigate } from '../../routing';
import { AlertModal } from '../Modal/AlertModal';
import { CompanyIcon, LogoutIcon } from '../Icons';
import { useStyledTheme } from '../../styling';
import { FormConfigurationProvider, useCreateAndAddRefs, useRefs } from '../../..';

const HeaderTopBar = styled(Flex)`${({ theme }) => `
  height:${theme.headerHeight};
  background-color: ${theme.colours.primary[1]};
  align-items:center;
  padding-left:${theme.padding[6]};
  padding-right:${theme.padding[8]};
  gap:${theme.padding[6]};
  position: sticky;
  top: 0;
  z-index: 5;
  flex-shrink: 0;
`}`;

const HeaderLogo = styled(Flex)`
  align-items: center;
  height:100%;
  cursor: pointer;
`;

const UserOption = styled(Flex)`${({ theme }) => `
  align-items:center;
  margin:0;
  border-radius:0;
  background:${theme.primary};
  color:${theme.textPrimary};
  cursor:pointer;
  padding: ${theme.padding[2]};
  font-size:${theme.fontSize[1]};
  cursor: default;
  font-family: 'Open Sans';
  font-weight: ${theme.fontWeight.normal};
  line-height: ${theme.fontSize[3]};
`}`;

const UserInfoButton = styled(UserOption)`${({ theme }) => `
  background: ${theme.colours.primary[1]};
  color:${theme.textSecondary};
`}`;

// eslint-disable-next-line no-shadow
export enum HeaderRefNames {
  HEADER = 'header',
}

export const UserButton = styled(UserOption) <{ disabled?: boolean }>`${({ theme, disabled }) => `  
  background: ${theme.colours.neutral[10]};
  cursor: pointer;
  color:${disabled ? theme.colours.neutral[6] : theme.colours.neutral[2]};

  &:hover, &:focus  {
    background: ${theme.colours.secondary[6]};
  };

  &:focus {
    font-weight:${theme.fontWeight.bold};
  };

  &:active {
    background: ${theme.colours.secondary[5]};
  };
`}`;

const UserContainer = styled(Flex) <{ show: boolean }>`${({ theme, show }) => `
  height:100%;
  margin-bottom:${theme.padding[1]};
  border-bottom: ${theme.padding[1]} solid ${show ? theme.colours.secondary[1] : theme.colours.primary[1]};
`}`;

const UserInfo = styled.button`${({ theme }) => `
  ${FlexCss}
  gap: ${theme.padding[3]};
  align-items: center;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  padding-top: ${theme.padding[2]};
  font: inherit;
  cursor: pointer;
  outline: inherit;
  height: 100%;
`}`;

const UserOptions = styled.div<{ show: boolean }>`${({ theme, show }) => `
  display: ${show ? 'inline' : 'none'};
  position: absolute; 
  top: ${theme.headerHeight};
  right: 0;
  width: ${theme.headerUserMenuWidth};
  box-shadow:${theme.boxShadow[1]};
  border-radius: ${theme.borderRadius[1]};
  padding-top:${theme.padding[1]};
  padding-bottom:${theme.padding[1]};
  background:${theme.primary};
  margin-top:${theme.padding[1]};
`}`;

const ChildrenContainer = styled(Flex)`${({ theme }) => `
  ${FlexGrowCss};
  align-items:center;
  gap: ${theme.padding[2]};
`}`;

const UserIconCircle = styled(Flex) <{ isSmall?: boolean }>`${({ theme, isSmall }) => `
  align-items:center;
  justify-content:center;
  background: ${theme.colours.secondary[3]};
  height: ${isSmall ? theme.fontSize[6] : theme.fontSize[9]};
  width: ${isSmall ? theme.fontSize[6] : theme.fontSize[9]};
  border-radius: 50%;
`}`;

const UserIconText = styled.div<{ isSmall?: boolean }>`${({ theme, isSmall }) => `
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: ${theme.fontWeight.bolder};
  font-size: ${isSmall ? theme.userIconSmSize : theme.fontSize[2]};
  line-height: ${theme.fontSize[6]};
  color: ${theme.colours.primary[1]};
`}`;

const EnvText = styled.div`${({ theme }) => `
  font-size: ${theme.fontSize[8]};
  color: ${theme.colours.neutral[10]};
  text-transform: uppercase;
`}`;

export const UserButtonLeft = styled(Flex)`${() => `
  justify-content:center;
  width: 19%;
`}`;

const DropdownIcon = styled.i<{ collapsed: boolean }>` ${({ theme }) => `
  color: ${theme.colours.neutral[10]};
  font-size: ${theme.fontSize[3]};
  width: ${theme.fontSize[3]};
  height: ${theme.fontSize[6]};
`}`;

export const AccountIcon = styled.i.attrs(() => ({
  className: 'mi-user',
}))` ${({ theme }) => `
  font-size: ${theme.fontSize[2]};
  color: ${theme.colours.neutral[3]};
`}`;

function UserIcon({ user, isSmall }: { user: UserType, isSmall?: boolean }) {
  return (
    <UserIconCircle isSmall={isSmall}>
      <UserIconText isSmall={isSmall}>
        {user?.firstName?.charAt(0).toUpperCase()}
        {user?.lastName?.charAt(0).toUpperCase()}
      </UserIconText>
    </UserIconCircle>
  );
}

export type UserMenu = {
  title: string,
  icon: ReactNode,
  onClick: () => void
}

export function Header({
  children,
  onClickLogo,
  userMenuItems,
}: {
  children: ReactNode,
  onClickLogo?: () => void,
  userMenuItems?: UserMenu[]
}) {
  const [showUserOptions, setShowUserOptions] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const { logout } = useAuth();
  const isAuth = useIsAuth();
  const isExternalUser = useIsUserAuth();
  const isSSO = useIsMsalAuth();

  const { user } = useCombinedUser();
  const navigate = useStickySafeNavigate();
  const clickHomeLink = useProgrammaticLink('/');
  const theme = useStyledTheme();

  const onClickOutsideUserOptions = () => {
    setShowUserOptions(false);
  };

  const onClickCompany = () => {
    navigate(`/manage-user/${user?.companyId}`);
  };

  const onClickAccount = () => {
    navigate('/broker-user');
  };

  const onClickLogoLocal = onClickLogo ?? ((event?: MouseEvent<HTMLElement>) => {
    clickHomeLink(event);
  });

  const onClickLogout = () => {
    setShowLogoutModal(true);
  };

  const onClickMenuItem = (handler: () => void) => {
    setShowUserOptions(false);
    handler();
  };

  const headerRef = useCreateAndAddRefs(HeaderRefNames.HEADER);
  const currentEnv = process.env.REACT_APP_ENV;
  return (
    <>
      <HeaderTopBar id={HeaderRefNames.HEADER} ref={headerRef}>
        <HeaderLogo onClick={onClickLogoLocal}><Logo /></HeaderLogo>
        {currentEnv === 'production'
          ? <></>
          : <EnvText>{currentEnv}</EnvText>}
        <ChildrenContainer>
          {children}
        </ChildrenContainer>
        {isAuth && (
          <UserContainer show={showUserOptions}>
            <OutsideClicker onClickOutside={onClickOutsideUserOptions}>
              <UserInfo onClick={() => setShowUserOptions(!showUserOptions)}>
                {user && (
                  <>
                    <UserIcon user={user} />
                    <DropdownIcon
                      className={showUserOptions ? 'mi-chevron-up' : 'mi-chevron-down'}
                      collapsed={showUserOptions}
                    />
                  </>
                )}
              </UserInfo>
              <UserOptions show={showUserOptions}>
                <UserInfoButton>
                  <UserButtonLeft>
                    <UserIcon user={user} isSmall />
                  </UserButtonLeft>
                  {`${user?.firstName} ${user?.lastName}`}
                </UserInfoButton>
                {userMenuItems?.map((menuItem) => (
                  <UserButton key={menuItem.title} onClick={() => onClickMenuItem(menuItem.onClick)}>
                    <UserButtonLeft>{menuItem.icon}</UserButtonLeft>
                    {menuItem.title}
                  </UserButton>
                ))}
                {isExternalUser && isUserWithRoles([UserRoles.BROKER_ADMIN], user) && (
                  <UserButton onClick={onClickCompany}>
                    <UserButtonLeft><CompanyIcon fill={theme.colours.neutral[3]} /></UserButtonLeft>
                    Company
                  </UserButton>
                )}
                {!isSSO && (
                  <UserButton onClick={onClickAccount}>
                    <UserButtonLeft><AccountIcon /></UserButtonLeft>
                    Account
                  </UserButton>
                )}
                <UserButton onClick={() => onClickMenuItem(onClickLogout)}>
                  <UserButtonLeft><LogoutIcon fill={theme.colours.neutral[3]} /></UserButtonLeft>
                  Log out
                </UserButton>
              </UserOptions>
            </OutsideClicker>
          </UserContainer>
        )}
      </HeaderTopBar>
      <FormConfigurationProvider isReadOnlyInput={false}>
        <AlertModal
          isOpen={showLogoutModal}
          title="Log out"
          content="Are you sure you want to log out?"
          onClose={() => setShowLogoutModal(false)}
          onConfirm={logout}
          confirmLabel="Log out"
          cancelLabel="Cancel"
        />
      </FormConfigurationProvider>
    </>
  );
}

export const useHeaderHeight = () => {
  const { refs } = useRefs();

  return refs[HeaderRefNames.HEADER]?.current?.clientHeight ?? 0;
};
